import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import _ from 'lodash';
import fp from 'lodash/fp';
import { connect } from 'react-redux';
import { size, media, color } from '@deerplusofficial/pos-helper';
import { EmbedScanner, Alert } from '@deerplusofficial/pos-components';
import { withQueryProducts } from '../../model/Queries';
import Select from '../Select/Select';
import Dimmer from '../Dimmer/Dimmer';
import { productLineShape } from '../../model/Types';
import actions from '../../actions';
import Product from './Product';
import CategoryTabSelector from './CategoryTabSelector';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  padding-bottom: ${size.toolbarToggle};
  background-color: #ffffff;
  ${media(
    'tablet',
    css`
      margin-right: 18px;
      padding-bottom: 0;
      position: relative;
    `
  )};
`;

const CategoryList = styled.div`
  padding: 10px 16px 0;
  ${media(
    'tablet',
    css`
      padding: 0;
    `
  )};
`;

const CategorySelector = styled(Select).attrs({
  className: 'hide-on-tablet',
})`
  height: 50px;
`;

const ProductCategory = styled.div`
  display: none;
  ${media(
    'tablet',
    css`
      display: block;
      margin-bottom: 8px;
      color: ${color.text.title};
    `
  )};
`;

const ProductList = styled.div`
  flex-grow: 1;
  padding: 8px ${size.productGap} ${size.cartToggle};
  overflow: auto;
  ${media(
    'tablet',
    css`
      padding: 20px 0 ${size.productGap} ${size.productGap};
    `
  )};
`;

const getCategoryInfo = fp.map(category => ({
  text: category.name,
  value: category.id,
}));

class ProductLine extends PureComponent {
  static propTypes = {
    searchKeyword: PropTypes.string,
    lineups: productLineShape,
    cart: PropTypes.shape(),
  };

  static defaultProps = {
    searchKeyword: '',
    lineups: [],
    cart: {},
  };

  state = {
    selectedCategory: 'all',
    isAlertActive: false,
  };

  componentDidMount() {
    this.props.fetchProducts();
  }

  onCategoryChange = ({ target: { value } }) =>
    this.setState({ selectedCategory: value }, () => window.scrollTo(0, 0));

  onScan = data => {
    const { items } = this.props;
    const item = _.find(
      items,
      ({ sku, barcode }) => sku === data || barcode === data
    );

    if (!item) {
      console.warn('Input not found', data);
      return;
    }

    this.onAddToCart(item);
  };

  onAddToCart = item => {
    const { cart, addCartItem, editCartItem } = this.props;
    const { storage, id, order_when_no_stock } = item;
    const itemInCart = _.get(cart.items, [id]);
    const quantity = _.get(itemInCart, ['quantity'], 0);

    if (quantity >= storage && !order_when_no_stock) {
      this.setState({ isAlertActive: true }, () => {
        Alert({
          type: 'error',
          text: '產品已無庫存，無法加入購物車！',
          onClose: () => this.setState({ isAlertActive: false }),
        });
      });
      return;
    }

    if (itemInCart) {
      editCartItem({ ...itemInCart, id, quantity: quantity + 1 });
      return;
    }

    addCartItem({ ...item, quantity: 1 });
  };

  matchSearchKeywords = ({ name = '', barcodes = '', skus = '' }) => {
    const { searchKeyword } = this.props;

    if (!_.size(searchKeyword)) return true;

    const fuzzyPattern = new RegExp(searchKeyword, 'i');
    const precisePattern = new RegExp(`\\b${searchKeyword}\\b`, 'i');

    return (
      fuzzyPattern.test(name) || precisePattern.test(`${barcodes} ${skus}`)
    );
  };

  get categories() {
    return [
      { text: '全部', value: 'all' },
      ...getCategoryInfo(this.props.lineups),
    ];
  }

  get filteredCategory() {
    const { selectedCategory } = this.state;
    const { lineups } = this.props;

    if (selectedCategory === 'all') return lineups;

    return _.filter(lineups, { id: selectedCategory });
  }

  get isScannerActive() {
    const { scanner, productModal, discountModal } = this.props;

    return _.every(
      [scanner.active, !productModal.active, !discountModal.active],
      Boolean
    );
  }

  renderProducts = products => {
    const { cart } = this.props;

    return _.map(products, product => {
      const { id, name, barcodes, skus, ...props } = product;
      const quantity = _.sum(
        _.map(
          cart.items,
          ({ productId, quantity }) => productId === id && quantity
        )
      );

      if (!this.matchSearchKeywords({ name, barcodes, skus })) return null;

      return (
        <Product key={id} id={id} name={name} quantity={quantity} {...props} />
      );
    });
  };

  render() {
    const { selectedCategory, isAlertActive } = this.state;
    const { lineups } = this.props;

    if (_.size(lineups) === 0) return <Dimmer dark />;

    return (
      <Container>
        <CategoryList>
          <CategorySelector
            onChange={this.onCategoryChange}
            options={this.categories}
          />
          <CategoryTabSelector
            onChange={this.onCategoryChange}
            options={this.categories}
            selected={selectedCategory}
          />
        </CategoryList>
        <ProductList>
          {_.map(this.filteredCategory, category => {
            return (
              <React.Fragment key={category.id}>
                <ProductCategory>{category.name}</ProductCategory>
                {this.renderProducts(category.products)}
              </React.Fragment>
            );
          })}
        </ProductList>
        {this.isScannerActive && (
          <EmbedScanner disabled={isAlertActive} onDetect={this.onScan} />
        )}
      </Container>
    );
  }
}

const { add: addCartItem, edit: editCartItem } = actions.cart;

export default fp.compose(
  withQueryProducts(),
  connect(
    ({ products: { lineups, items }, ...state }) => ({
      lineups,
      items,
      ..._.pick(state, [
        'searchKeyword',
        'scanner',
        'productModal',
        'discountModal',
        'cart',
      ]),
    }),
    { addCartItem, editCartItem }
  )
)(ProductLine);



// WEBPACK FOOTER //
// ./src/components/ProductLine/ProductLine.js